import React, {
  createElement,
  Fragment,
  FunctionComponent,
  useState,
  useRef,
} from 'react';
import {
  Button as RaButton,
  useNotify,
  useRefresh,
  useTranslate,
  useUnselectAll,
  useUpdateMany,
  useCreate,
  useDeleteMany,
  useRecordContext,
} from 'react-admin';
import { isEmpty, get } from 'lodash';
import { useHistory } from 'react-router';
import { ModalForm } from '../../../modals';
import { ModalFormButtonProps } from './types';
import { Input as ConfigInput } from '../../../../config/types/field';
import useConfig from '../../../../hooks/useConfig';
import getUrlPaths from '../../../../helpers/getUrlPath';
import { getIconComponentFromName } from '../../../../helpers/icons';
import { getErrorMessage } from '../../../../helpers/error';
import { DELETE, CREATE, UPDATE } from '../../../../constants/httpMethod';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  DialogContentText as MuiDialogContentText,
} from '@material-ui/core';
import checkConditions from '../../../../helpers/conditions';

const useStyles = makeStyles({
  button: {
    marginLeft: '10px',
  },
  errorContent: {
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
  },
  delete: {
    border: 'none',
    color: '#DC004E',
    backgroundColor: 'rgba(220, 0, 78, 0.04)',
    '&:hover': {
      border: 'none',
      backgroundColor: 'rgba(220, 0, 78, 0.15)',
    },
  },
});

const ModalFormButton: FunctionComponent<ModalFormButtonProps> = (props) => {
  const [showDialog, setShowDialog] = useState(false);
  // Initialize synchronously (during render, before first paint) so the skip count
  // is ready before any post-remount click events fire. useEffect would be too late
  // because it runs after the browser paints — a MUI focus-restoration click could
  // arrive in that window and open the modal before the count is restored.
  const skipOpenCountRef = useRef(
    (() => {
      const r = props.resource;
      const n = props.name;
      if (r && n) {
        const key = `skip_modal_${r}_${n}`;
        const saved = parseInt(sessionStorage.getItem(key) || '0', 10);
        if (saved > 0) {
          sessionStorage.removeItem(key);

          return saved;
        }
      }

      return 0;
    })()
  );
  const config = useConfig();
  const t = useTranslate();
  const {
    selectedIds = [],
    modal,
    resource,
    name,
    extraUrl,
    label,
    confirmation,
    values,
    httpMethod = UPDATE,
    data,
    checkUnsavedChanges,
  } = props;
  const classes = useStyles();
  const history = useHistory();

  if (!selectedIds || !resource) return null;

  const resourceInstance = config.getResource(resource);
  const record = useRecordContext();
  const inputs: any[] = (() => {
    if (modal?.inputs) {
      return Object.entries(modal.inputs).map(([key, input]) => ({
        ...resourceInstance.getAttribute(key),
        ...(input as ConfigInput),
        source: key,
      }));
    }

    return [];
  })();

  const refresh = useRefresh();
  const notify = useNotify();
  const unselectAll = useUnselectAll();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const urlPaths = getUrlPaths(selectedIds, extraUrl);
  const isInvoice = values?.createInvoice;
  const [create] = useCreate(
    resource,
    {},
    {
      onSuccess: () => {
        if (get(props, 'redirectToTabsNumber')) {
          updateTabsStateAndRedirect();
        }
        refresh();
        notify('notify.success.create');
      },
      onFailure: () => notify('notify.error.create', 'error'),
    }
  );

  const [deleteMany, { loading: loadingDelete }] = useDeleteMany();

  const [updateMany, { loading }] = useUpdateMany(
    resource,
    urlPaths,
    {},
    {
      onSuccess: () => {
        refresh();
        notify('notify.success.update');
        unselectAll(resource);
      },
      onFailure: (errors: Record<string, any>) => {
        if (Array.isArray(errors) && errors.length) {
          errors.forEach((error) => {
            const errorBody = get(error, 'error.body');
            if (get(errorBody, 'error') === 'RuntimeError') {
              setErrorMessage(get(errorBody, 'message'));
            } else {
              const msg = getErrorMessage(error);
              if (typeof msg !== 'undefined') {
                notify(`notify.error.${msg}`, 'error', { id: error.id });
              }
            }
          });
        } else {
          notify('notify.error.update', 'error');
        }
      },
    }
  );

  // Logic to add state in the history router state to select the current tabs
  // in the Tabs component after refreshing the page
  function updateTabsStateAndRedirect() {
    const currentState = get(history, 'location.pathname.state');
    const redirectToTabsNumber = get(props, 'redirectToTabsNumber');

    const newState = {
      ...currentState,
      ...(redirectToTabsNumber && { currentTabs: redirectToTabsNumber }),
    };
    history.replace(get(history, 'location.pathname'), newState);
  }

  function handleClick(formValues: Record<string, any>) {
    const formattedFormValues = {
      ...formValues,
      ...values,
      resource_selected_id: selectedIds,
    };
    setShowDialog(false);
    if (httpMethod === CREATE) {
      const data = isInvoice
        ? {
            contract_milestone_id: record.contract_milestone_id,
            user_kind: record.user_kind,
          }
        : formattedFormValues;

      return create({ payload: { data } });
    }
    if (httpMethod === UPDATE) {
      updateMany(resource, urlPaths, formValues);
    }

    if (httpMethod === DELETE) {
      const ids = [selectedIds] as string[];
      if (!isEmpty(ids)) {
        deleteMany(resource, ids);
      }
      // Refresh the page after delete to avoid errors
      refresh();
      if (get(props, 'redirectToTabsNumber')) {
        updateTabsStateAndRedirect();
      }
      notify('notify.success.delete');
    }
  }

  function handleOpenModalClick() {
    if (skipOpenCountRef.current > 0) {
      skipOpenCountRef.current--;

      return;
    }
    if (
      checkUnsavedChanges &&
      ((window as any).__form_dirty__ || (window as any).__charges_modified__)
    ) {
      setErrorMessage(t('notify.error.unsaved_changes'));

      return;
    }
    setShowDialog(true);
  }

  function handleCloseModalClick() {
    setShowDialog(false);
  }

  const IconComponent = getIconComponentFromName(get(props, 'icon'));
  const isDisabled =
    loading ||
    loadingDelete ||
    (props.disabled && record
      ? checkConditions(
          {
            rules: props.disabled,
            satisfy: 'ANY',
          },
          record
        )
      : false);

  function renderButton() {
    if (IconComponent) {
      return (
        <RaButton
          disabled={loading || loadingDelete}
          onClick={handleOpenModalClick}
          variant="outlined"
          className={classes.delete}
        >
          {createElement(IconComponent)}
        </RaButton>
      );
    }

    return (
      <RaButton
        disabled={isDisabled}
        label={label}
        onClick={handleOpenModalClick}
        variant="outlined"
        className={classes.button}
      />
    );
  }

  return (
    <Fragment>
      {renderButton()}
      <ModalForm
        open={showDialog}
        title={confirmation?.title && t(confirmation.title)}
        handleClick={handleClick}
        handleClose={handleCloseModalClick}
        inputs={inputs}
        resource={resource}
        content={confirmation?.content}
      />
      <MuiDialog
        open={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        disableRestoreFocus
      >
        <MuiDialogTitle>{t('notify.error.title')}</MuiDialogTitle>
        <MuiDialogContent>
          <MuiDialogContentText className={classes.errorContent}>
            {errorMessage}
          </MuiDialogContentText>
        </MuiDialogContent>
        <MuiDialogActions>
          <RaButton
            onClick={(e) => {
              e.stopPropagation();
              setErrorMessage(null);
              (window as any).__reload_after_save__ = true;
              const submit = (window as any).__form_submit__;
              if (submit) submit();
            }}
            label={t('ra.action.save')}
          />
          <RaButton
            onClick={(e) => {
              e.stopPropagation();
              setErrorMessage(null);
            }}
            label={t('modal.confirm.cancel')}
          />
        </MuiDialogActions>
      </MuiDialog>
    </Fragment>
  );
};

export default ModalFormButton;
